import type { Project } from '@/types'

// Personal projects only. Things I built (or am building) on my own
// initiative. Published work, employer engagements, and external articles
// belong in src/data/blog.ts (Writing) instead.
export const projects: Project[] = [
  {
    id: 'stochastic-inventory-explorer',
    title: 'Stochastic Inventory Explorer',
    description:
      'A web app that optimizes reorder policies over thousands of simulated demand futures, not a single forecast.',
    longDescription:
      'Most inventory tools size safety stock off a single demand forecast, an average that says nothing about the days that actually cause stockouts. This app instead treats the reorder decision as a stochastic optimization problem: it scores about 240 candidate (r, Q) and (s, S) policies against 1,000 Monte Carlo simulations of a 180-day future built from empirical demand, then returns the cheapest policy that hits a reliability target you choose, a technique called Sample Average Approximation. It runs on three real point-of-sale datasets (two Walmart items from the M5 competition and a heavy-tailed UK gift-shop SKU from UCI Online Retail II), chosen specifically to break different intuitions: a clean baseline, an intermittent-demand item where cycle service level and fill rate diverge, and a fat-tailed item where the closest achievable reliability tops out around 93.5%, short of the usual 95% target. Every run returns a cost-vs-reliability Pareto frontier and a fan chart of simulated inventory paths, plus a plain-English breakdown of the recommendation and a side-by-side comparison against textbook safety-stock rules. The same pipeline also ships as a Jupyter notebook for anyone who would rather read the Python than click through the UI.',
    technologies: [
      'Python',
      'NumPy',
      'FastAPI',
      'React',
      'TypeScript',
      'Vite',
      'Docker',
      'Google Cloud Run',
    ],
    category: 'optimization',
    featured: true,
    image: '/inventory-frontier-thumb.png',
    writeupSlug: 'stochastic-inventory-explorer',
    demoUrl: 'https://inventory.christopherrobertwhite.com',
    tags: ['Stochastic Optimization', 'Monte Carlo', 'Sample Average Approximation', 'Web App'],
    challenges: [
      'Choosing grid search plus Monte Carlo over an MIP formulation, since the policy space is small and the empirical demand distribution would reduce to scenario sampling anyway',
      'Making cycle service level vs. fill rate legible for intermittent-demand SKUs, where the two metrics tell very different stories',
      'Failing gracefully on the heavy-tailed scenario, where the closest achievable policy still falls short of the requested reliability target',
    ],
    outcomes: [
      'A live web app that scores ~240 candidate policies against 1,000 simulated futures in a couple of seconds, fast enough to feel interactive rather than batch',
      'Three real POS scenarios, each picked to teach a different lesson about reliability',
      "A parallel Jupyter notebook exposing the same optimizer end-to-end, for anyone who'd rather read the Python",
    ],
    date: '2026-08-12',
  },
  {
    id: 'pto-optimizer',
    title: 'PTO Calendar Optimizer',
    description:
      'A free web app that plans your PTO for you. A CP-SAT solver finds three optimized strategies to choose from.',
    longDescription:
      'Vacation planning is a combinatorial problem, so I formulated it and let a solver decide. Each workday gets a binary PTO variable, and CP-SAT maximizes an adjacency-driven objective that rewards bridging holidays, full weeks off, and cheap travel windows. Budget, locked dates, blackout dates, and a monthly cap all come in as hard constraints. The number of vacation blocks K is an exact constraint too, and the app solves for every K from 3 to 15 to trace a Pareto frontier, then picks three strategies off of it: Long Weekends, Balanced (at the knee), and Deep Rest. It supports 15+ countries with automatic holiday detection. You can drag-to-rank holiday priorities, and every plan gets scored against a travel cost index (built from BLS airfare seasonality and holiday-week premiums) and re-scored on six human-aligned 0-100 dimensions. It ships as a FastAPI backend serving a React SPA, in a single Docker container behind Caddy.',
    technologies: [
      'Python',
      'OR-Tools CP-SAT',
      'FastAPI',
      'Pandas',
      'React',
      'Vite',
      'Tailwind',
      'Docker',
    ],
    category: 'optimization',
    featured: true,
    image: '/pto-calendar-thumb.png',
    writeupSlug: 'pto-optimizer-cp-sat',
    demoUrl: 'https://pto.christopherrobertwhite.com',
    tags: ['Constraint Programming', 'CP-SAT', 'Web App', 'Live Demo'],
    challenges: [
      'Enforcing an exact vacation-block count K with linearized block-start indicators',
      'Keeping the travel cost signal a tiebreaker (~5% of objective) so it never overrides holiday bridging',
      'Translating solver objective values into six interpretable plan scores people can compare',
    ],
    outcomes: [
      'Live at pto.christopherrobertwhite.com, free to use, 15+ countries with automatic holiday detection',
      'Three optimized strategies per run, picked from a K=3 to K=15 Pareto sweep',
      'Single-container FastAPI + React deployment behind Caddy',
    ],
    date: '2026-07-14',
  },
  {
    id: 'dual-hop-vpn-terraform-v2ray',
    title: 'Dual-Hop VPN with Terraform and V2Ray',
    description:
      'A self-hosted VPN with a relay hop and an exit hop in two AWS regions. One terraform apply stands it all up.',
    longDescription:
      'Built ahead of a trip to a country where I knew I would run into network restrictions, as an alternative to trusting a commercial VPN. Traffic flows through two hops: a relay node in one AWS region takes in the encrypted traffic and hands it off to an exit node in a different region, the one that actually talks to the open internet. From the outside, the relay looks like an ordinary HTTPS site serving static content, so there is nothing obvious to fingerprint or block. A single `terraform apply` provisions both EC2 instances and handles TLS certificates via a Cloudflare DNS challenge. It also drops a ready-to-import client config onto your machine, and tearing everything down is just as quick. A small Python CLI generates a VMess URI and QR code so a phone client can be set up in about ten seconds.',
    technologies: [
      'Terraform',
      'AWS',
      'V2Ray',
      'Nginx',
      'Docker',
      'Certbot',
      'Cloudflare',
      'Python',
    ],
    category: 'infra',
    featured: true,
    image: '/vpn-thumb.png',
    writeupSlug: 'dual-hop-vpn-terraform-v2ray',
    githubUrl: 'https://github.com/chrisrwhite/v2ray-dual-region',
    tags: ['Networking', 'Terraform', 'Infrastructure as Code', 'Security'],
    challenges: [
      'Making the relay indistinguishable from an ordinary HTTPS static site to avoid fingerprinting',
      'Automating TLS issuance across two regions via a Cloudflare DNS challenge',
      'Keeping the entire stack to a single one-command deploy and teardown',
    ],
    outcomes: [
      'One-command deploy: a single terraform apply provisions both nodes and a ready-to-import client config',
      'Python CLI generates a VMess URI and QR code for phone setup in about ten seconds',
      'Open-sourced on GitHub after months of daily personal use',
    ],
    date: '2025-07-15',
  },
]
